import { useEffect, useState } from "react";

import {
  TextField,
  Typography,
  Select,
  Checkbox,
  DatePicker,
  TextEditor,
  DocumentList,
  DocumentsModal,
  Button,
} from "@king-ict/eupisi-ui/components";
import { CodeBook, UploadFile } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  getAnnouncementById,
  saveAnnouncement,
  getAnnouncementDocumentById,
} from "../../api/Announcement/AnnouncementApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
} from "../../components";
import { ValidationFailure, Role } from "../../models";
import { Announcement } from "../../models/Announcement";
import { FaqApplicationDisplay } from "../../models/Faq/Group";
import { Attachment } from "../../models/Shared/Attachment";
import { announcementDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function AnnouncementEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const createDefaultItem = (): Announcement => {
    return {
      id: 0,
      title: "",
      text: "",
      dateFrom: new Date(),
      status: true,
      isSticky: false,
      isPortalVisible: false,
      applicationDisplay: 1,
    };
  };
  const [item, setItem] = useState<Announcement>(createDefaultItem());
  const [applicationDisplays] = useState<CodeBook[]>(() => {
    const arrayObjects = [] as CodeBook[];
    for (const [propertyKey, propertyValue] of Object.entries(
      FaqApplicationDisplay
    )) {
      var num = Number(propertyKey);
      if (Number.isNaN(Number(propertyKey))) {
        continue;
      }
      var tranKey = "common.faq.faqApplicationDisplay." + propertyValue;
      var trans = t(tranKey);
      arrayObjects.push({ id: num, name: trans });
    }
    return arrayObjects;
  });
  const [roles] = useState<CodeBook[]>(() => {
    const arrayObjects = [] as CodeBook[];
    for (const [propertyKey, propertyValue] of Object.entries(Role)) {
      var num = Number(propertyKey);
      if (Number.isNaN(Number(propertyKey))) {
        continue;
      }
      var tranKey = "role." + propertyValue;
      var trans = t(tranKey);
      arrayObjects.push({ id: num, name: trans });
    }
    return arrayObjects;
  });
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (!isNaN(+id) && +id > 0) {
        var result = await getAnnouncementById(+id);
        if (result) {
          setItem(result);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleTitleChange = (e: any) => {
    const title = e.target.value;
    const newItem: Announcement = {
      ...item,
      title: title,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleTextChange = (val: string) => {
    const newItem: Announcement = {
      ...item,
      text: val,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleDateFromEventChange = (e: any) => {
    const date = new Date(e.target.value);
    const newItem: Announcement = {
      ...item,
      dateFrom: date,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleIsStickyChange = () => {
    const isSticky = item.isSticky;
    const newItem: Announcement = {
      ...item,
      isSticky: !isSticky,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleApplicationDisplayChange = (e: any) => {
    const applicationDisplay = Number.parseInt(e.target.value);
    const newItem: Announcement = {
      ...item,
      applicationDisplay: applicationDisplay,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleRoleChange = (e: any) => {
    const roleList = e.target.value as number[];
    const newItem: Announcement = {
      ...item,
      roleDisplay: roleList && roleList.length > 0 ? roleList : undefined,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleStatusChange = () => {
    const status = item.status;
    const newItem: Announcement = {
      ...item,
      status: !status,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleIsPortalVisibleChange = () => {
    const s = item.isPortalVisible;
    const newItem: Announcement = {
      ...item,
      isPortalVisible: !s,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleUploadFileChange = (files: UploadFile[]) => {
    const newItem: Announcement = {
      ...item,
      uploadFiles: files ?? undefined,
    };
    setItem(newItem);
    dispatch(setIsEdited());
    setShowUploadModal(false);
  };
  const handleFileChange = async (files: File[]) => {
    var uploadedFiles: UploadFile[] = [];
    if (files && files.length > 0) {
      files.forEach((file) => {
        const fileReader = new FileReader();
        fileReader.readAsBinaryString(file);
        var uploadedFile = {
          fileName: file.name,
          fileData: fileReader.result,
        } as UploadFile;
        uploadedFiles.push(uploadedFile);
      });
    }
    const newItem: Announcement = {
      ...item,
      uploadFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleGetDocument = (doc: Attachment) => {
    if (doc && doc.id) {
      getAnnouncementDocumentById(doc.id);
    }
  };
  const handleRemoveDocument = (doc: Attachment) => {
    if (doc && doc.id) {
      const newItem: Announcement = {
        ...item,
        attachments: item.attachments?.filter((val) => val.id !== doc.id),
      };
      setItem(newItem);
      dispatch(setIsEdited());
    }
  };
  if (!item.text && id !== "0") return null;

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("common.announcement.pageName")}
        errors={errors}
      />
      <FormCard>
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.announcement.title")}
              name={"item.title"}
              value={item.title}
              onChange={handleTitleChange}
              errorText={
                errors?.find((e) => e.propertyName === "Title")?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextEditor
              label={t("common.announcement.text")}
              initialValue={item.text}
              handleChange={handleTextChange}
              errorText={
                errors?.find((e) => e.propertyName === "Text")?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <DatePicker
              label={t("common.announcement.dateFrom")}
              name={"item.dateFrom"}
              onChange={handleDateFromEventChange}
              value={item.dateFrom}
              showTimePicker={true}
              errorText={
                errors?.find((e) => e.propertyName === "DateFrom")?.message
              }
              className="mt-2"
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox
              label={t("common.announcement.isSticky")}
              name={nameof<typeof item>("isSticky")}
              checked={item.isSticky}
              onChange={handleIsStickyChange}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Select
              label={t("common.announcement.applicationDisplay")}
              name={nameof<typeof item>("applicationDisplay")}
              value={item.applicationDisplay}
              onChange={handleApplicationDisplayChange}
              selectData={codeBookToSelectData(applicationDisplays)}
              errorText={
                errors?.find((e) => e.propertyName === "ApplicationDisplay")
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Select
              label={t("common.announcement.roleDisplay")}
              name={nameof<typeof item>("roleDisplay")}
              value={item.roleDisplay}
              onChange={handleRoleChange}
              selectData={codeBookToSelectData(roles)}
              errorText={
                errors?.find((e) => e.propertyName === "AnnouncementAppRoles")
                  ?.message
              }
              multiple
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Checkbox
              label={t("common.announcement.isPortalVisible")}
              name={nameof<typeof item>("isPortalVisible")}
              checked={item.isPortalVisible}
              onChange={handleIsPortalVisibleChange}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Checkbox
              label={t("common.announcement.active")}
              name={nameof<typeof item>("status")}
              checked={item.status}
              onChange={handleStatusChange}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Typography variant="medium" component="div" color="light">
              {t("common.announcement.attachments")}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <DocumentList
              documents={item.attachments ?? []}
              onDocumentClick={handleGetDocument}
              onRemoveClick={handleRemoveDocument}
            />
          </Col>
          <Col md={4}>
            <Button
              color="primary"
              onClick={() => setShowUploadModal(true)}
              size="sm"
            >
              {t("accessibility.documentModal.uploadFile")}
            </Button>
          </Col>
          <Col md={4}>
            <DocumentList
              documents={item.uploadFiles ?? []}
              removable={false}
            />
          </Col>
        </Row>
      </FormCard>

      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        redirectUrlSaveOK={announcementDetailUrl}
        handleSave={async () => saveAnnouncement(item)}
        setSaveErrors={setErrors}
      />
      <DocumentsModal
        title={t("accessibility.documentModal.title")}
        infoPanelText={t("accessibility.documentModal.info")}
        types={t("accessibility.documentModal.types")}
        allDocuments={item.uploadFiles ?? []}
        maxSize={10}
        filesAction={handleFileChange}
        hideOldDocuments={true}
        open={showUploadModal}
        onConfirm={handleUploadFileChange}
        onClose={() => setShowUploadModal(false)}
        btnCancelText={t("common.quit")}
        btnOkText={t("common.addNew")}
      />
    </ContentArea>
  );
}
